import type { ReactNode } from 'react'

/**
 * Pratinjau naskah — W1-F04.
 *
 * Merender bagian Markdown yang benar-benar dipakai naskah tata tertib dan
 * pakta integritas: judul, paragraf, daftar bernomor, daftar butir, dan tebal.
 *
 * Dibangun sebagai elemen React, **bukan** lewat `dangerouslySetInnerHTML`.
 * Konsekuensinya penting: sekalipun kelak ada isi yang lolos dari penolakan
 * markah di server, isi itu tetap muncul sebagai teks dan tidak pernah menjadi
 * markah aktif. Dua lapis pertahanan, dan yang kedua tidak bergantung pada yang
 * pertama.
 *
 * CATATAN: aplikasi peserta pada Wave 2 harus memakai renderer yang sama persis,
 * bila tidak, yang dipratinjau panitia berbeda dari yang dibaca peserta. Saat
 * itu berkas ini pindah ke paket bersama.
 */

/** Memecah `**tebal**` menjadi potongan teks dan <strong>. */
function inline(text: string): ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        part
      ),
    )
}

interface Block {
  readonly kind: 'h1' | 'h2' | 'h3' | 'p' | 'ol' | 'ul'
  readonly lines: string[]
}

function parse(markdown: string): Block[] {
  const blocks: Block[] = []
  let current: Block | null = null

  const push = (): void => {
    if (current !== null) blocks.push(current)
    current = null
  }

  for (const raw of markdown.split('\n')) {
    const line = raw.trimEnd()

    if (line.trim() === '') {
      push()
      continue
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    if (heading !== null) {
      push()
      const level = heading[1]?.length ?? 1
      blocks.push({
        kind: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3',
        lines: [heading[2] ?? ''],
      })
      continue
    }

    const ordered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (ordered !== null) {
      if (current?.kind !== 'ol') push()
      current ??= { kind: 'ol', lines: [] }
      current.lines.push(ordered[1] ?? '')
      continue
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line)
    if (bullet !== null) {
      if (current?.kind !== 'ul') push()
      current ??= { kind: 'ul', lines: [] }
      current.lines.push(bullet[1] ?? '')
      continue
    }

    if (current?.kind !== 'p') push()
    current ??= { kind: 'p', lines: [] }
    current.lines.push(line)
  }

  push()
  return blocks
}

export function NaskahPreview({ title, body }: { title: string; body: string }): ReactNode {
  return (
    <article className="naskah-preview">
      <h1>{title}</h1>
      {parse(body).map((block, i) => {
        switch (block.kind) {
          case 'h1':
            return <h2 key={i}>{inline(block.lines[0] ?? '')}</h2>
          case 'h2':
            return <h3 key={i}>{inline(block.lines[0] ?? '')}</h3>
          case 'h3':
            return <h4 key={i}>{inline(block.lines[0] ?? '')}</h4>
          case 'ol':
            return (
              <ol key={i}>
                {block.lines.map((line, j) => (
                  <li key={j}>{inline(line)}</li>
                ))}
              </ol>
            )
          case 'ul':
            return (
              <ul key={i}>
                {block.lines.map((line, j) => (
                  <li key={j}>{inline(line)}</li>
                ))}
              </ul>
            )
          default:
            return <p key={i}>{inline(block.lines.join(' '))}</p>
        }
      })}
    </article>
  )
}

/** Diekspor untuk pengujian; bukan bagian antarmuka komponen. */
export const __test = { parse, inline }
